import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import type { FunctionComponent } from "react";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";

import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import ProjectRevenueInvoiceEntry
	from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueInvoiceEntry/ProjectRevenueInvoiceEntry.tsx";
import {
	ProjectRevenueSplitDonut,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueSplitDonut/ProjectRevenueSplitDonut.tsx";
import type {
	ProjectRevenueTimelineRow,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import {
	formatMonthLabel,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { getPhaseOrderPath, getPhasePath } from "~/modules/project/utils/projectDetailsViewUtils.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

const MONTH_DONUT_SIZE = 16;
const MONTH_DONUT_RING_WIDTH = 4.5;
const BREAKDOWN_DONUT_SIZE = 14;
const BREAKDOWN_DONUT_RING_WIDTH = 3.6;

type Props = {
	row: ProjectRevenueTimelineRow;
	projectId: string;
	phasesById: Record<string, ProjectPhase>;
	ordersById: Record<string, OrderWithBillingSettings>;
	isExpanded: boolean;
	onToggle: (month: string) => void;
};

const ProjectRevenueMonthRow: FunctionComponent<Props> = ({
	row,
	projectId,
	phasesById,
	ordersById,
	isExpanded,
	onToggle,
}) => {
	const { month, monthData, invoices } = row;
	const isExpandable = !!monthData && monthData.phasesBreakdown.length > 0;

	const sortedPhases = monthData
		? [...monthData.phasesBreakdown].sort(
			(a, b) => (phasesById[b.phaseId]?.phaseNumber ?? 0) - (phasesById[a.phaseId]?.phaseNumber ?? 0),
		)
		: [];

	const expandedRowCount = sortedPhases.reduce(
		(total, phaseBreakdown) => total + 1 + phaseBreakdown.orderBreakdowns.length,
		0,
	);
	const invoicesRowSpan = isExpanded ? 1 + expandedRowCount : 1;
	const mainCellsAlignTopClass = isExpanded || invoices.length > 1 ? "align-top" : "";
	return <tbody className="group/month border-b border-gray-100">
	<tr className="text-sm transition-colors hover:bg-gray-200 group-hover/month:bg-gray-200">
		<td className={clsx("px-2 py-3", mainCellsAlignTopClass)}>
			<button type="button"
					disabled={!isExpandable}
					onClick={() => onToggle(month)}
					className="flex w-full items-center gap-x-2 disabled:cursor-default">
				{isExpandable
					? (isExpanded ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />)
					: <span className="size-4" />}
				<span className="font-medium capitalize text-gray-900">{formatMonthLabel(month)}</span>
			</button>
		</td>
		<td className={clsx("px-4 py-3 text-right font-semibold text-gray-900", mainCellsAlignTopClass)}>
			<div className="flex items-center justify-end gap-x-2">
				<span>{formatCentsToCurrency(monthData?.totalTrackedBillableCents ?? 0)}</span>
				<ProjectRevenueSplitDonut split={monthData?.billingSplit}
										  size={MONTH_DONUT_SIZE}
										  ringWidth={MONTH_DONUT_RING_WIDTH} />
			</div>
		</td>
		<td className={clsx("px-4 py-3 text-right text-gray-400", mainCellsAlignTopClass)}>
			{formatCentsToCurrency(monthData?.totalTrackedNotBillableCents ?? 0)}
		</td>
		<td className="px-4 py-3 align-top"
			rowSpan={invoicesRowSpan}>
			<div className="flex flex-col gap-y-2">
				{invoices.map((invoice) => <ProjectRevenueInvoiceEntry key={invoice.invoiceId}
																	   invoice={invoice}
																	   projectId={projectId} />)}
			</div>
		</td>
	</tr>

	{isExpanded && sortedPhases.map((phaseBreakdown) => {
		const phase = phasesById[phaseBreakdown.phaseId];
		const phaseTitle = phase ? getFullPhaseTitle(phase) : "Unbekannte Phase";

		return <Fragment key={phaseBreakdown.phaseId}>
			<tr className="transition-colors  group-hover/month:bg-gray-200">
				<td className="px-2 py-1 pl-8 text-xs font-semibold text-gray-800">
					{phase
						? <NavLink className="hover:text-primary-500 hover:underline"
								   to={getPhasePath(projectId, phase.phaseNumber)}>{phaseTitle}</NavLink>
						: phaseTitle}
				</td>
				<td className="px-4 py-1 text-right text-xs font-medium text-gray-800">
					<div className="flex items-center justify-end gap-x-2">
						<span>{formatCentsToCurrency(phaseBreakdown.totalTrackedBillableCents)}</span>
						<ProjectRevenueSplitDonut split={phaseBreakdown.billingSplit}
												  size={BREAKDOWN_DONUT_SIZE}
												  ringWidth={BREAKDOWN_DONUT_RING_WIDTH} />
					</div>
				</td>
				<td className="px-4 py-1 text-right text-xs font-medium text-gray-400">
					{formatCentsToCurrency(phaseBreakdown.totalTrackedNotBillableCents)}
				</td>
			</tr>

			{phaseBreakdown.orderBreakdowns.map((orderBreakdown) => {
				const orderTitle = ordersById[orderBreakdown.orderId]?.title ?? "Unbekannter Auftrag";

				return <tr key={orderBreakdown.orderId}
						   className="bg-gray-50/60 transition-colors  group-hover/month:bg-gray-200">
					<td className="px-2 py-1 pl-14 text-xs text-gray-600">
						{phase
							? <NavLink className="hover:text-primary-500 hover:underline"
									   to={getPhaseOrderPath(projectId, phase.phaseNumber, orderBreakdown.orderId)}>{orderTitle}</NavLink>
							: orderTitle}
					</td>
					<td className="px-4 py-1 text-right text-xs font-light text-gray-700">
						<div className="flex items-center justify-end gap-x-2">
							<span>{formatCentsToCurrency(orderBreakdown.totalTrackedBillableCents)}</span>
							<ProjectRevenueSplitDonut split={orderBreakdown.billingSplit}
													  size={BREAKDOWN_DONUT_SIZE}
													  ringWidth={BREAKDOWN_DONUT_RING_WIDTH} />
						</div>
					</td>
					<td className="px-4 py-1 text-right text-xs font-light text-gray-400">
						{formatCentsToCurrency(orderBreakdown.totalTrackedNotBillableCents)}
					</td>
				</tr>;
			})}
		</Fragment>;
	})}
	</tbody>;
};

export default ProjectRevenueMonthRow;
